export default {
  name: "BolideAssistant",

  props: {
    is_open: Boolean,
    tiles: {
      type: Array,
      default: () => []
    },
    active_menu_item: {
      type: String,
      default: "portfolio-comparisons"
    },
    breadcrumb: {
      type: Array,
      default: () => []
    }
  },

  emits: ["close-assistant"],

  data() {
    return ({
      user_message: "",
      messages: [
        {
          sender: "assistant",
          text: "Hello! I'm NPI Assist. I can help you understand the Dashboard, Company Profile, Verification, Certificates & Reports, Programmes, Integration, Settings, and Help sections.",
        },
      ],
    });
  },

  computed: {
    system_sections() {
      return [
        { key: "portfolio-comparisons", label: "Dashboard", help: "Use Dashboard to view the main performance overview and live site activity." },
        { key: "company-profile", label: "Company Profile", help: "Use Company Profile to review organisation information and company details." },
        { key: "verification", label: "Verification", help: "Use Verification to validate supplier, identity, and compliance information." },
        { key: "certificates-reports", label: "Certificates & Reports", help: "Use Certificates & Reports to review certificates, supporting files, and generated reports." },
        { key: "programmes", label: "Programmes", help: "Use Programmes to follow programme progress, milestones, and updates." },
        { key: "integration", label: "Integration", help: "Use Integration to review connected services and verification history." },
        { key: "settings", label: "Settings", help: "Use Settings to manage user, role, and system configuration details." },
        { key: "help", label: "Help", help: "Use Help to find support guidance and assistance." }
      ];
    },

    current_section_label() {
      const current_section = this.system_sections.find((section) => section.key === this.active_menu_item);
      return current_section?.label || this.breadcrumb.at(-1)?.title || "Dashboard";
    },

    live_site_count() {
      return this.tiles.filter((tile) => tile?.is_live).length;
    },

    live_site_names() {
      return this.tiles
        .filter((tile) => tile?.is_live)
        .slice(0, 5)
        .map((tile) => tile.title)
        .filter(Boolean);
    }
  },

  watch: {
    messages() {
      this.$nextTick(() => {
        const el = this.$refs.chat_body;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    is_open(isOpen) {
      document.body.classList.toggle("chatbot-lock", isOpen);
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.handleClickOutside);
    document.addEventListener("touchstart", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    document.removeEventListener("touchstart", this.handleClickOutside);
    document.body.classList.remove("chatbot-lock");
  },

  methods: {
    close_assistant() {
      this.$emit("close-assistant");
    },

    handleClickOutside(event) {
      if (!this.is_open) return;

      const panel = this.$refs.chatbot;
      if (panel && !panel.contains(event.target)) {
        this.close_assistant();
      }
    },

    send() {
      if (!this.user_message.trim()) return;

      const text = this.user_message.trim();
      this.messages.push({ sender: "user", text });
      this.user_message = "";

      setTimeout(() => {
        this.generate_reply(text.toLowerCase());
      }, 350);
    },

    reply(text) {
      this.messages.push({ sender: "assistant", text });
    },

    get_system_overview() {
      const section_names = this.system_sections.map((section) => section.label).join(", ");
      const site_names = this.live_site_names.join(", ");
      const site_summary = this.live_site_count
        ? ` I can also see ${this.live_site_count} live sites in the dashboard, including ${site_names}.`
        : "";

      return `The NPI system includes these sections: ${section_names}.${site_summary}`;
    },

    get_section_help(section_key) {
      const section = this.system_sections.find((item) => item.key === section_key);
      return section ? section.help : "";
    },

    generate_reply(question) {
      if (
        question.includes("what is inside")
        || question.includes("what's inside")
        || question.includes("inside the system")
        || question.includes("what can you do")
        || question.includes("modules")
        || question.includes("sections")
      ) {
        return this.reply(this.get_system_overview());
      }

      if (question.includes("where am i") || question.includes("current page") || question.includes("current section")) {
        return this.reply(`You are currently in ${this.current_section_label}.`);
      }

      if (question.includes("dashboard")) {
        return this.reply(this.get_section_help("portfolio-comparisons"));
      }

      if (question.includes("company profile")) {
        return this.reply(this.get_section_help("company-profile"));
      }

      if (question.includes("verification")) {
        return this.reply(this.get_section_help("verification"));
      }

      if (question.includes("certificate") || question.includes("report")) {
        return this.reply(this.get_section_help("certificates-reports"));
      }

      if (question.includes("programme")) {
        return this.reply(this.get_section_help("programmes"));
      }

      if (question.includes("integration")) {
        return this.reply(this.get_section_help("integration"));
      }

      if (question.includes("setting")) {
        return this.reply(this.get_section_help("settings"));
      }

      if (question.includes("help") || question.includes("support")) {
        return this.reply(this.get_section_help("help"));
      }

      if (question.includes("live") || question.includes("site")) {
        return this.reply(
          this.live_site_count
            ? `There are ${this.live_site_count} live sites in the dashboard. Some examples are ${this.live_site_names.join(", ")}.`
            : "I can help explain the NPI sections like Dashboard, Verification, Integration, Programmes, Settings, and Help."
        );
      }

      return this.reply(
        `I can help you navigate the NPI system and explain what is available in each section. You are currently in ${this.current_section_label}.`
      );
    },
  },
};
